import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", end: true },
  {
    label: "Services",
    dropdown: [
      { groupHeader: "Core Chaplaincy Services" },
      { to: "/services/workplace-chaplaincy", label: "Workplace Chaplaincy" },
      { to: "/services/crisis-grief-bereavement", label: "Crisis, Grief & Bereavement" },
      { groupHeader: "Healthcare & Care Settings" },
      { to: "/services/healthcare-hospice", label: "Healthcare" },
      { to: "/services/senior-living", label: "Senior Living / Board & Care" },
      { groupHeader: "Industries We Serve" },
      { to: "/industries/automotive-dealerships", label: "Automotive & Dealerships" },
      { to: "/industries/restaurants-hospitality", label: "Restaurants, Fast-Food Franchises & Hospitality" },
      { to: "/industries/construction-skilled-trades", label: "Construction & Skilled Trades" },
      { to: "/industries/real-estate-brokerages-title-services", label: "Real Estate, Brokerages & Title Services" },
    ],
  },
  {
    label: "Blog",
    dropdown: [
      { to: "/resources/articles", label: "Articles & Insights" },
      { to: "/resources", label: "Free Workforce Resources" },
    ],
  },
  { to: "/about", label: "About" },
  {
    to: "/for-organizations",
    label: "For Organizations",
    dropdown: [
      { groupHeader: "Employee Support Options" },
      { to: "/callachap", label: "CALLACHAP — On-Demand Employee Support" },
      { to: "/services/leadership-employee-support", label: "Leadership / Employee Support" },
      { to: "/packages", label: "Care Team Packages" },
      { groupHeader: "Organizational Services & Learning" },
      { to: "/services/training-workshops", label: "Training & Workshops" },
      { to: "/for-organizations#chaplaincy-vs-eap", label: "Chaplaincy vs. EAP" },
      { to: "/books", label: "Books & Organizational Resources" },
      { groupHeader: "About Working With CCCHAPS" },
      { to: "/for-organizations#organizations-supported", label: "Organizations We've Supported" },
      { to: "/faq", label: "Frequently Asked Questions" },
    ],
  },
  { to: "/contact", label: "Contact" },
];

export default function PublicNavbar() {
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="public-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 lg:h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="public-navbar-logo" aria-label="California Corporate Chaplains — home">
          <img src="/ccchaps-logo.png" alt="California Corporate Chaplains" className="h-10 w-10 lg:h-11 lg:w-11 object-contain" />
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-heading font-semibold text-slate-900 text-sm tracking-tight">California Corporate Chaplains</span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">CCCHAPS</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {NAV.map((item) =>
            item.dropdown ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                {item.to ? (
                  <NavLink
                    to={item.to}
                    end={item.end}
                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-1 whitespace-nowrap px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isActive ? "text-[#4A6FA5] bg-[#F0F4F8]" : "text-slate-700 hover:text-slate-900 hover:bg-slate-50"
                      }`
                    }
                  >
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
                  </NavLink>
                ) : (
                  <button
                    className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50"
                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                )}
                {openDropdown === item.label && (
                  <div className="absolute left-0 top-full pt-2 w-96" data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-2">
                      {item.dropdown.map((d, i) =>
                        d.groupHeader ? (
                          <div
                            key={`group-${i}`}
                            data-testid={`nav-group-${d.groupHeader.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={i === 0 ? "" : "mt-2 pt-2 border-t border-slate-100"}
                          >
                            <p className="px-3 pt-1 pb-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
                              {d.groupHeader}
                            </p>
                          </div>
                        ) : (
                          <Link
                            key={d.to}
                            to={d.to}
                            data-testid={`nav-sub-${d.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="block px-3 py-2.5 rounded-md text-sm text-slate-700 hover:bg-[#F0F4F8] hover:text-[#3B597B]"
                            onClick={() => setOpenDropdown(null)}
                          >
                            {d.label}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "text-[#4A6FA5] bg-[#F0F4F8]" : "text-slate-700 hover:text-slate-900 hover:bg-slate-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden xl:flex items-center gap-2">
          <Link
            to="/care-portal"
            data-testid="nav-care-portal"
            data-analytics-cta="care-portal"
            className="inline-flex items-center whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-800 hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
          >
            Care Portal
          </Link>
          <Link
            to="/schedule"
            data-testid="nav-consultation"
            data-analytics-cta="request-consultation"
            className="inline-flex items-center whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium bg-[#4A6FA5] text-white hover:bg-[#3B597B]"
          >
            Request Consultation
          </Link>
        </div>

        <button
          className="xl:hidden inline-flex items-center justify-center w-10 h-10 rounded-md text-slate-700 hover:bg-slate-100"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="public-navbar-mobile-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-slate-200 bg-white max-h-[calc(100vh-4rem)] overflow-y-auto" data-testid="public-navbar-mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {NAV.map((item) =>
              item.dropdown ? (
                <div key={item.label} className="py-1">
                  {item.to ? (
                    <Link
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className="block px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <div className="px-3 py-2 text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">{item.label}</div>
                  )}
                  {item.dropdown.map((d, i) =>
                    d.groupHeader ? (
                      <div
                        key={`m-group-${i}`}
                        data-testid={`nav-mobile-group-${d.groupHeader.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`${i === 0 ? "mt-1" : "mt-2 pt-2 border-t border-slate-100"} px-4 pb-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500`}
                      >
                        {d.groupHeader}
                      </div>
                    ) : (
                      <Link
                        key={d.to}
                        to={d.to}
                        onClick={() => setOpen(false)}
                        className="block px-4 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-50"
                      >
                        {d.label}
                      </Link>
                    )
                  )}
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="block px-3 py-2.5 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  {item.label}
                </Link>
              )
            )}
            <div className="pt-3 mt-2 border-t border-slate-100 flex flex-col gap-2">
              <Link to="/care-portal" onClick={() => setOpen(false)} data-analytics-cta="care-portal" className="block w-full text-center px-4 py-2.5 rounded-lg text-sm font-medium border border-slate-300 text-slate-800">
                Care Portal
              </Link>
              <Link to="/schedule" onClick={() => setOpen(false)} data-analytics-cta="request-consultation" className="block w-full text-center px-4 py-2.5 rounded-lg text-sm font-medium bg-[#4A6FA5] text-white">
                Request Consultation
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
